import React, {
  ButtonHTMLAttributes,
  AnchorHTMLAttributes,
  ReactNode,
} from "react";
import { LucideIcon } from "lucide-react";

interface BaseButtonProps {
  variant?: "default" | "light" | "white";
  size?: "xsmall" | "small" | "large";
  full?: boolean;
  icon?: LucideIcon;
  children?: ReactNode;
  className?: string;
}

interface ButtonAsButtonProps
  extends BaseButtonProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  href?: never;
}

interface ButtonAsLinkProps
  extends BaseButtonProps,
    Omit<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      keyof BaseButtonProps | "className"
    > {
  href: string;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export function Button(props: ButtonProps) {
  const {
    variant = "default",
    size,
    full = false,
    icon: Icon,
    children,
    className,
    ...restProps
  } = props;

  const disabled = "disabled" in props ? props.disabled : false;
  const href = "href" in props ? props.href : undefined;

  const baseClasses = [
    "rounded-md",
    "px-3.5",
    "py-2.5",
    "text-base",
    "font-semibold",
    "inline-flex",
    "items-center",
    "space-x-1",
    "focus-visible:outline",
    "focus-visible:outline-2",
    "focus-visible:outline-offset-2",
    "focus-visible:outline-default-600",
    "[&>span]:truncate",
    "[&>span]:flex-shrink",
    "[&>svg]:flex-shrink-0",
    "[&>svg]:w-4",
    "[&>svg]:h-4",
  ];

  const variantClasses = {
    white:
      "text-default-600 bg-white hover:bg-slate-50 text-default-800 border border-gray-200 shadow-sm !text-gray-500",
    default: "bg-default-600 text-white hover:bg-default-500",
    light: "bg-default-50 text-default-600 hover:bg-default-100",
  };

  const sizeClasses = {
    xsmall: [
      "px-2.5",
      "py-1",
      "[&>span]:text-xs",
      "[&>svg]:pl-[-10px]",
      "[&>svg]:flex-shrink-0",
      "[&>svg]:w-3",
      "[&>svg]:h-3",
      "space-x-1",
    ],
    small: [
      "text-lg",
      "px-2.5",
      "py-1.5",
      "space-x-2",
      "text-sm",
      "[&>svg]:pl-[-10px]",
      "[&>svg]:flex-shrink-0",
      "[&>svg]:w-4",
      "[&>svg]:h-4",
    ],
    large: [
      "text-lg",
      "px-4",
      "py-3",
      "space-x-2",
      "[&>svg]:pl-[-10px]",
      "[&>svg]:flex-shrink-0",
      "[&>svg]:w-6",
      "[&>svg]:h-6",
    ],
  };

  const classes = [
    ...baseClasses,
    variantClasses[variant],
    ...(size ? sizeClasses[size] : []),
    disabled ? "pointer-events-none opacity-50" : "",
    full ? "w-full justify-center" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {Icon && <Icon className="flex-shrink-0" />}
      {children && <span className="truncate flex-shrink">{children}</span>}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={classes}
        {...(restProps as Omit<
          AnchorHTMLAttributes<HTMLAnchorElement>,
          "className"
        >)}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={classes}
      disabled={disabled}
      {...(restProps as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
}
